// SmartTable core helpers (pure functions, unit tested).
//
// SmartTable adds per-user column controls to any SFM table:
//   * resize a column (drag the right edge of its header)
//   * hide / show a column
//   * filter rows by the text in any visible column
//
// These are VIEW controls only. They never change, delete, or re-order
// records, and filters are not saved between visits so a returning user
// never sees a silently reduced record set. Widths and hidden columns are
// saved per user, per browser, per table.

export const MIN_WIDTH = 48;
const STORAGE_PREFIX = "sfm.tablecols.v1";

// Visible header text: skips aria-hidden parts and the screen-reader-only
// "sorted ascending/descending" text sortable headers add, so a column keeps
// the same name whichever way the table is sorted.
export function cellLabel(th) {
  if (!th) return "";
  if (th.getAttribute && th.getAttribute("data-st-label")) return th.getAttribute("data-st-label");
  const clone = th.cloneNode(true);
  clone.querySelectorAll('[aria-hidden="true"]').forEach((n) => n.remove());
  return (clone.textContent || "")
    .replace(/sorted (ascending|descending)/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Header labels, one per column, read from the last header row.
export function headerLabels(tableEl) {
  if (!tableEl) return [];
  const rows = tableEl.querySelectorAll(":scope > thead > tr");
  if (!rows.length) return [];
  const ths = Array.from(rows[rows.length - 1].children);
  const seen = {};
  return ths.map((th, i) => {
    const t = cellLabel(th) || `Column ${i + 1}`;
    seen[t] = (seen[t] || 0) + 1;
    return seen[t] > 1 ? `${t} (${seen[t]})` : t;
  });
}

// Numeric / uuid-like path segments are replaced so one choice applies to
// every job, not one job at a time. A tableId, when given, is the key.
export function normalizePath(path) {
  return String(path || "")
    .split("/")
    .map((seg) => (/^\d+$/.test(seg) || /^[0-9a-f-]{16,}$/i.test(seg) ? ":id" : seg))
    .join("/");
}

export function storageKey(path, labels, id) {
  return id ? `${STORAGE_PREFIX}:id:${id}` : `${STORAGE_PREFIX}:${normalizePath(path)}:${labels.join("|")}`;
}

// SmartTable only supports simple headers: one header row, no merged cells.
// Anything else keeps the plain table so columns can never misalign.
export function headerIsSimple(tableEl) {
  if (!tableEl) return false;
  const rows = tableEl.querySelectorAll(":scope > thead > tr");
  if (rows.length !== 1) return false;
  return Array.from(rows[0].children).every((th) => (th.colSpan || 1) === 1 && (th.rowSpan || 1) === 1);
}

// Locked columns can never be hidden, even if an old saved setting says so.
export function effectiveHidden(hidden, locked) {
  const lock = new Set(locked || []);
  return (hidden || []).filter((h) => !lock.has(h));
}

export function loadPrefs(key) {
  try {
    const raw = window.localStorage.getItem(key);
    const p = raw ? JSON.parse(raw) : null;
    return {
      hidden: Array.isArray(p?.hidden) ? p.hidden : [],
      widths: p && typeof p.widths === "object" && p.widths ? p.widths : {},
    };
  } catch {
    return { hidden: [], widths: {} };
  }
}

export function savePrefs(key, prefs) {
  try {
    const empty = !prefs.hidden.length && !Object.keys(prefs.widths).length;
    if (empty) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify({ hidden: prefs.hidden, widths: prefs.widths }));
  } catch {
    /* storage unavailable: controls still work for this visit */
  }
}

// Scoped CSS for hidden columns and widths. Cells with a colspan (empty
// states, loading rows, expanded detail rows) are never touched.
// minWidths: { [label]: px } floors that saved or default widths can never go
// below (e.g. an Action column whose buttons must stay fully visible).
export function buildCss(scope, labels, prefs, minWidths = {}) {
  const t = `table.${scope}`;
  const out = [
    `${t} > thead > tr > th:not(.MuiTableCell-stickyHeader){position:relative;}`,
    `${t} > thead > tr:last-child > th::after{content:"";position:absolute;right:0;top:20%;height:60%;width:3px;border-right:2px solid rgba(127,127,127,.35);cursor:col-resize;}`,
    `${t} > tbody > tr[data-st-hide]{display:none;}`,
  ];
  const hidden = new Set(prefs.hidden);
  const anyWidth = labels.some((l) => prefs.widths[l]);
  let total = 0;
  labels.forEach((label, i) => {
    const n = i + 1;
    const cells = `${t} > thead > tr:last-child > th:nth-child(${n}), ${t} > tbody > tr > td:nth-child(${n}):not([colspan]), ${t} > tfoot > tr > td:nth-child(${n}):not([colspan])`;
    if (hidden.has(label)) {
      out.push(`${cells}{display:none;}`);
      return;
    }
    const w = prefs.widths[label];
    if (anyWidth) {
      const px = Math.max(MIN_WIDTH, Math.round(w || 120), Math.round((minWidths && minWidths[label]) || 0));
      total += px;
      out.push(`${cells}{box-sizing:border-box;width:${px}px;min-width:${px}px;max-width:${px}px;overflow:hidden;overflow-wrap:anywhere;}`);
    }
  });
  if (anyWidth) out.push(`${t}{table-layout:fixed;width:${total}px !important;min-width:0 !important;}`);
  // Screen only: a printout always shows every column and every row.
  return `@media screen{\n${out.join("\n")}\n}`;
}

// True when every active filter's text appears in that column's cell.
export function rowMatches(cellTexts, filtersByIndex) {
  return Object.entries(filtersByIndex).every(([i, q]) => {
    const needle = String(q || "").trim().toLowerCase();
    if (!needle) return true;
    return String(cellTexts[Number(i)] || "").toLowerCase().includes(needle);
  });
}
